import { useMemo, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import {
  Question,
  Range,
} from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';
import { QuestionTypes } from '@viome/viome-base/src/utils/enum';
import RadioButtonWrapper from './question-types/RadioButtonWrapper';
import MultiSelectWrapper from './question-types/MultiSelectWrapper';
import InputFieldWrapper from './question-types/InputFieldWrapper';
import DatePickerWrapper from './question-types/DatePickerWrapper';
import SearchFieldWrapper from './question-types/SearchFieldWrapper';
import SliderWrapper from './question-types/SliderWrapper';
import MultipleInputWrapper from './question-types/MultipleInputWrapper';
import { InfoSheet } from '../HealthPages/InfoSheet';
import TimeInputWrapper from './question-types/TimeInputWrapper';
import { ReadOnlyLockIcon } from '@/components/icons/ReadOnlyLockIcon';
import { Image } from '@/components/design-system/Image';
import { Skeleton } from '@/components/design-system/Skeleton';

interface QuestionRendererProps {
  question: Question;
  control: Control<FieldValues>;
  disabled?: boolean;
}

export const QuestionRenderer = ({
  question,
  control,
  disabled = false,
}: QuestionRendererProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const questionId = question.id.toString();
  const { detailed_info: detailedInfo, answer_type: questionType } =
    question || {};
  const answerMetaData = question?.answer_meta_data;

  // Memoize the component selection to avoid recalculation on every render
  const QuestionComponent = useMemo(() => {
    let component;
    const measurementRanges = answerMetaData?.range as Range[];

    switch (questionType) {
      case QuestionTypes.MULTIPLE_CHOICE_SINGLE_SELECTION:
        component = (
          <RadioButtonWrapper
            key={`single-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            options={answerMetaData?.choices || []}
            required={question.is_required}
            disabled={disabled}
          />
        );
        break;

      case QuestionTypes.MULTIPLE_CHOICE_MULTI_SELECTION:
        component = (
          <MultiSelectWrapper
            key={`multi-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            options={answerMetaData?.choices || []}
            required={question.is_required}
            maxSelections={answerMetaData?.max_selection}
            disabled={disabled}
            canSortSelection={answerMetaData?.can_sort_selection}
          />
        );
        break;

      case QuestionTypes.TEXT:
        component = (
          <InputFieldWrapper
            key={`text-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            placeholder={question.answer_meta_data?.placeholder}
            required={question.is_required}
            disabled={disabled}
            type="text"
            ranges={question.answer_meta_data?.validation || undefined}
            multiline={question.answer_meta_data?.multiline || false}
          />
        );
        break;

      case QuestionTypes.NUMBER_INPUT:
        component = (
          <InputFieldWrapper
            key={`number-input-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            placeholder={question.answer_meta_data?.placeholder}
            required={question.is_required}
            disabled={disabled}
            type={'number'}
            ranges={question.answer_meta_data?.validation || undefined}
          />
        );
        break;

      case QuestionTypes.MEASUREMENT_DIAL:
        component = (
          <InputFieldWrapper
            key={`measurement-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            placeholder={answerMetaData?.placeholder}
            required={question.is_required}
            ranges={measurementRanges || undefined}
            disabled={disabled}
            type="number"
          />
        );
        break;

      case QuestionTypes.DATE_DIAL:
        component = (
          <DatePickerWrapper
            key={`date-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            required={question.is_required}
            minDate={question.answer_meta_data?.start_date}
            maxDate={question.answer_meta_data?.end_date}
            disabled={disabled}
            defaultDate={question.answer_meta_data?.default_date}
          />
        );
        break;

      case QuestionTypes.SEARCH_MULTI: {
        const choices =
          question.answer_meta_data?.choices?.map((choice) => ({
            id: choice.value,
            label: choice.label,
            value: choice.value,
          })) || [];

        // Check if answer is a skip answer
        const skipAnswer =
          question.answer &&
          typeof question.answer === 'object' &&
          'can_skip_value' in question.answer
            ? { can_skip_value: question.answer.can_skip_value }
            : undefined;

        component = (
          <SearchFieldWrapper
            key={`search-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            placeholder={question.answer_meta_data?.placeholder}
            options={choices}
            required={question.is_required}
            disabled={disabled}
            canSkip={question.answer_meta_data?.can_skip}
            skipLabel={question.answer_meta_data?.can_skip_label}
            initialAnswer={skipAnswer}
          />
        );
        break;
      }

      case QuestionTypes.SLIDER:
        component = (
          <SliderWrapper
            key={`slider-${question.id}`}
            control={control}
            name={questionId}
            subtitle={question.info}
            required={question.is_required}
            ranges={question.answer_meta_data?.range as number[] | undefined}
            disabled={disabled}
            startValueRepresent={
              question.answer_meta_data?.start_value_represent
            }
            endValueRepresent={question.answer_meta_data?.end_value_represent}
          />
        );
        break;

      case QuestionTypes.MULTIPLE_INPUT: {
        const inputs = question?.answer_meta_data?.input || [];

        // Check if answer is a skip answer
        const multipleInputSkipAnswer =
          question.answer &&
          typeof question.answer === 'object' &&
          'can_skip_value' in question.answer
            ? { can_skip_value: question.answer.can_skip_value }
            : undefined;

        component = (
          <MultipleInputWrapper
            key={`multiple-input-${question.id}`}
            control={control}
            name={questionId}
            inputs={inputs}
            required={question.is_required}
            disabled={disabled}
            canSkip={question.answer_meta_data?.can_skip}
            skipLabel={question.answer_meta_data?.can_skip_label}
            initialAnswer={multipleInputSkipAnswer}
          />
        );
        break;
      }

      case QuestionTypes.CALENDAR_DATE:
        component = (
          <DatePickerWrapper
            key={`calendar-date-${question.id}`}
            control={control}
            name={questionId}
            title={question.title}
            subtitle={question.info}
            required={question.is_required}
            minDate={question.answer_meta_data?.start_date}
            maxDate={question.answer_meta_data?.end_date}
            disabled={disabled}
            defaultDate={question.answer_meta_data?.default_date}
          />
        );
        break;

      case QuestionTypes.TIME_PICKER:
        component = (
          <TimeInputWrapper
            key={`time-${question.id}`}
            control={control}
            name={questionId}
            subtitle={question.info}
            placeholder={question.answer_meta_data?.placeholder}
            required={question.is_required}
            disabled={disabled}
            ranges={question.answer_meta_data?.range as Range[]}
          />
        );
        break;

      default:
        component = (
          <div className="text-red-500 p-4 bg-red-50 rounded-lg">
            <p className="font-medium text-center">
              Unsupported question type: {questionType}
            </p>
            <p className="text-sm text-center mt-1 opacity-80">
              Question ID: {questionId} - {question.title}
            </p>
          </div>
        );
    }

    return component;
  }, [questionType, question, control, disabled]);

  return (
    <div className="mb-8 animate-fadeIn">
      {question?.prompt && (
        <p className="typography-headline3 text-primary mb-6 whitespace-pre-line">
          {question?.prompt}
        </p>
      )}
      <h2 className="typography-h1 !font-light text-primary mb-6">
        {question.title}
        {detailedInfo && (
          <InfoSheet title="" description={detailedInfo} mode="light" />
        )}
      </h2>
      {question?.info && (
        <p className="typography-body text-secondary mb-6 whitespace-pre-line">
          {question.info}
        </p>
      )}
      {disabled && (
        <div
          className="flex mb-6"
          data-testid="questionnaire-readOnlyIndicator"
        >
          <ReadOnlyLockIcon />
          <div className="font-sans typography-body text-secondary ml-2">
            Read only
          </div>
        </div>
      )}
      {answerMetaData?.image_url && (
        <div className="mb-6 flex justify-center items-center relative w-full">
          {isImageLoading && (
            <Skeleton className="h-[361px] md:h-[446px] w-full max-w-[446px] absolute inset-0 mx-auto" />
          )}
          <Image
            src={answerMetaData?.image_url}
            alt={question.title}
            className="h-[361px] md:h-[446px] w-full max-w-[446px] object-contain"
            onLoad={() => setIsImageLoading(false)}
            style={{ opacity: isImageLoading ? 0 : 1 }}
          />
        </div>
      )}
      {QuestionComponent}
    </div>
  );
};
